import { LiveOpsConfigResult, LiveOpsRawConfig } from './LiveOpsApi'
import { ExperimentAssignment, LiveOpsSection, resolveLiveOpsSection } from './resolveLiveOps'

export interface RawLiveOpsResponse {
  client: LiveOpsSection | null
  configVersion: string
  serverTimeMs: number
}

export interface LiveOpsHooks {
  /** Bucketing unit — the current profile id. Undefined = experiments skipped. */
  getUnitId?: () => string | undefined
  /** Fired once per (unitId, experimentId, variantId) per SDK lifetime, after resolution. */
  onExposure?: (assignment: ExperimentAssignment, configVersion: string) => void
}

/**
 * Once-per-(unit, experiment, variant) gate shared by the cache and the mock API.
 * Keyed on unitId so a mid-session identity change re-logs for the new profile.
 */
export class ExposureDeduper {
  private seen = new Set<string>()

  constructor(private onExposure?: LiveOpsHooks['onExposure']) {}

  record(unitId: string, assignments: ExperimentAssignment[], configVersion: string): void {
    if (!this.onExposure) return
    for (const a of assignments) {
      const key = `${unitId} ${a.experimentId} ${a.variantId}`
      if (this.seen.has(key)) continue
      this.seen.add(key)
      try {
        this.onExposure(a, configVersion)
      } catch {
        // Exposure logging must never break config resolution.
      }
    }
  }
}

const DEFAULT_MAX_AGE_MS = 60_000

interface Entry {
  raw: RawLiveOpsResponse
  fetchedAtLocalMs: number
}

/**
 * Shared rule cache + server-anchored resolution used by both network impls
 * (Http/Rpc). Caches the raw client rules + the server clock stamp + local fetch
 * time; re-resolves on every read against `serverTimeMs + elapsed-local`, so a
 * scheduled flip is always live even while the rules stay cached.
 */
export class LiveOpsCache {
  private entry: Entry | null = null
  private deduper: ExposureDeduper

  constructor(
    private fetchRaw: () => Promise<RawLiveOpsResponse>,
    private hooks?: LiveOpsHooks,
  ) {
    this.deduper = new ExposureDeduper(hooks?.onExposure)
  }

  clear(): void {
    this.entry = null
  }

  private async ensureFresh(maxAgeMs: number): Promise<Entry> {
    const now = Date.now()
    if (this.entry && maxAgeMs > 0 && now - this.entry.fetchedAtLocalMs < maxAgeMs) {
      return this.entry
    }
    const raw = await this.fetchRaw()
    this.entry = { raw, fetchedAtLocalMs: Date.now() }
    return this.entry
  }

  /** server-anchored now: server clock at fetch + elapsed local time since. */
  private effectiveNow(entry: Entry): number {
    return entry.raw.serverTimeMs + (Date.now() - entry.fetchedAtLocalMs)
  }

  async getConfig(maxAgeMs = DEFAULT_MAX_AGE_MS): Promise<LiveOpsConfigResult> {
    const entry = await this.ensureFresh(maxAgeMs)
    const unitId = this.hooks?.getUnitId?.()
    const resolved = resolveLiveOpsSection(
      entry.raw.client ?? undefined,
      this.effectiveNow(entry),
      unitId,
    )
    if (unitId !== undefined) {
      this.deduper.record(unitId, resolved.assignments, entry.raw.configVersion)
    }
    return { ...resolved, configVersion: entry.raw.configVersion }
  }

  async getRaw(maxAgeMs = DEFAULT_MAX_AGE_MS): Promise<LiveOpsRawConfig> {
    const entry = await this.ensureFresh(maxAgeMs)
    return {
      values: entry.raw.client?.values ?? {},
      overrides: (entry.raw.client?.overrides ?? []) as LiveOpsRawConfig['overrides'],
      experiments: (entry.raw.client?.experiments ?? []) as LiveOpsRawConfig['experiments'],
      configVersion: entry.raw.configVersion,
      serverTimeMs: entry.raw.serverTimeMs,
    }
  }
}
